import { useState, useRef, useEffect, useContext } from "react";
import {
    MessageCircle,
    X,
    Send,
    Paperclip,
    Minimize2,
    Maximize2,
    FileText,
    RotateCcw,
    AlertTriangle,
    Link,
    ClipboardCheck,
    Check,
    Circle,
} from "lucide-react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { useNavigate } from "react-router-dom";
import { ChatWindowService } from "./ChatWindowService";
import { UserContext } from "../UserContext";
import { FileUpload } from "../FileUpload";
import { FileUploadLegacy } from "../FileUploadLegacy";
import type { PortalMortgageDocumentModel } from "../../models/PortalMortgageDocumentModel";
import type { Message, TimelineItem, ProfileConflict, ChatAgentResponse, UploadContext, ProgressSummary, ApplicantProgress } from "../../models/ChatWindowModels";
import { UploadResponse, DocAttributes } from "../../models/UploadResponse";
import "./ChatWindow.css";
import {
  Col,
  Container,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Row,
  Stack,
} from "react-bootstrap";
import context from "react-bootstrap/esm/AccordionContext";

const TypingIndicator = ({ label }: { label?: string }) => (
    <div className="chatwindow-typing">
        {label && <span className="chatwindow-typing__label">{label}</span>}
        <div className="typing-indicator">
            <div className="dot" />
            <div className="dot" />
            <div className="dot" />
        </div>
    </div>
);

let msgIdCounter = 0;
const nextMsgId = (suffix = "") => `${Date.now()}-${++msgIdCounter}${suffix}`;

export function ChatWindow({ embedded = false }: { embedded?: boolean } = {}) {
    const [isOpen, setIsOpen] = useState(embedded);
    const [isExpanded, setIsExpanded] = useState(true);
    const [messages, setMessages] = useState<Message[]>([]);
    const [inputValue, setInputValue] = useState("");
    const [inputState, setInputState] = useState<"text" | "name" | "none">("text");
    const [session, setSession] = useState<{
        applicationId: number;
        userName: string;
        agentState?: string;
    } | null>(null);
    const [activeConflicts, setActiveConflicts] = useState<ProfileConflict[]>([]);
    const [profileSummary, setProfileSummary] = useState<string>("");
    const [onboardingStep, setOnboardingStep] = useState<"details" | "timeline" | "property" | "done" | null>(null);
    const [isLoading, setIsLoading] = useState(false);
    const [showBooking, setShowBooking] = useState(false);
    const [allDocs, setAllDocs] = useState<PortalMortgageDocumentModel[]>([]);
    const [showProgress, setShowProgress] = useState(false);
    const [activePill, setActivePill] = useState<{
        uploadContext: UploadContext;
        mortgageDocumentId: string;
        contactId: string;
        category: string;
        name: string;
    } | null>(null);
    // Guided upload flow — walks through AIP document categories one at a time
    const aipCategories = [
        { key: "income", label: "Payslips / Proof of Income" },
        { key: "banking", label: "Bank Statements / Open Banking" },
    ];
    const [guidedCategoryIndex, setGuidedCategoryIndex] = useState(0);
    // T5: Track which option messages have been acted on, and which value was selected
    const [selectedOptionsByMsg, setSelectedOptionsByMsg] = useState<Record<string, string>>({});

    const [pendingPin, setPendingPin] = useState<{
        fileUrl: string;
        fileName: string;
        uploadId: string;
        mortgageDocumentId: string;
        uploadContext: UploadContext;
        category?: string;
        contactId?: string;
        pillName?: string;
    } | null>(null);

    const navigate = useNavigate();
    const messagesEndRef = useRef<HTMLDivElement>(null);
    const userCtx = useContext(UserContext);

    // Build user info from portal's UserContext
    const userName = userCtx?.contact?.fullName || "User";
    const mortgage = userCtx?.mortgage;

    const scrollToBottom = () => {
        messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    };

    useEffect(() => {
        scrollToBottom();
    }, [messages, isOpen, isExpanded]);

    // Initial Session & Greeting
    useEffect(() => {
        if (isOpen && !session) {
            setIsLoading(true);
            ChatWindowService.startSession(userName)
                .then(async (sess) => {
                    setSession({ applicationId: sess.applicationId, userName });

                    // T1: Load AIP documents from the dedicated endpoint
                    let docs: PortalMortgageDocumentModel[] = [];
                    if (mortgage?.mortgageId) {
                        try {
                            docs = await ChatWindowService.getAipDocuments(mortgage.mortgageId);
                            setAllDocs(docs);
                        } catch (err) {
                            console.warn("ChatWindow: failed to load AIP documents", err);
                        }
                    }

                    // Summarise full mortgage profile and store for use with every message
                    let summary = `User Name: ${userName}.`;
                    if (mortgage) {
                        try {
                            summary = await ChatWindowService.summariseProfile("agentContext", mortgage);
                        } catch (err) {
                            console.warn("ChatWindow: profile summarise failed, using basic context", err);
                        }
                    }
                    setProfileSummary(summary);

                    if (messages.length === 0) {
                        // T4: Check if user has already started uploading — skip to resume
                        const aipDocs = docs.filter(d => d.aipCategory);
                        const doneDocs = aipDocs.filter(isDocDone);

                        if (doneDocs.length > 0 && aipDocs.length > 0) {
                            setOnboardingStep("done");

                            // Show loading indicator while we fetch the AI summary
                            addMessage({ id: "1", role: "agent", text: "..." });

                            // Build progress data from the local docs variable (state may not have flushed)
                            const progressData = buildProgressSummaryFromDocs(docs);

                            // Fetch friendly AI summary with fallback
                            let aiText: string;
                            try {
                                aiText = mortgage
                                    ? await ChatWindowService.summariseProfile("clientSummary", mortgage)
                                    : `Welcome back, ${userName.split(" ")[0]}! Here's where things stand.`;
                            } catch {
                                const firstName = userName.split(" ")[0];
                                aiText = `Welcome back, ${firstName}! You've uploaded **${progressData.completedDocs} of ${progressData.totalDocs}** documents (${progressData.percentComplete}%). Let's keep going!`;
                            }

                            // Replace loading indicator with rich resume message
                            setMessages(prev => prev.filter(m => m.id !== "1"));
                            addMessage({
                                id: "1_resume",
                                role: "agent",
                                text: aiText,
                                progressSummary: progressData,
                                type: "options",
                                options: [
                                    { label: "Continue uploading", value: "onboarding_upload_docs" },
                                    { label: "Speak to your advisor", value: "book_call" },
                                ],
                            });
                        } else {
                        // Build a summary of what we know about the user
                        const firstName = userName.split(" ")[0];
                        const applicant = mortgage?.primaryApplicant;
                        const appType = mortgage?.subTypeFormatted || mortgage?.applicationType || "";
                        const isJoint = !!(mortgage?.secondaryApplicant?.contactId);
                        const isFirstMortgage = applicant?.previouslyHaveMortgageLoan === "No";
                        const variableIncome = [
                            applicant?.commission,
                            applicant?.overtime,
                            applicant?.bonus,
                            applicant?.otherIncome,
                        ].filter((v) => v != null && v > 0);
                        const hasVariableIncome = variableIncome.length > 0;

                        const fmt = (val: number) =>
                            val.toLocaleString("en-IE", { style: "currency", currency: "EUR", maximumFractionDigits: 0 });

                        let detailLines: string[] = [];
                        const secondApplicant = mortgage?.secondaryApplicant;
                        if (isJoint && secondApplicant?.fullName) {
                            detailLines.push(`- **Applicant 1:** ${userName}`);
                            detailLines.push(`- **Applicant 2:** ${secondApplicant.fullName}`);
                        } else if (userName && userName !== "User") {
                            detailLines.push(`- **Name:** ${userName}`);
                        }
                        if (applicant?.emailAddress) detailLines.push(`- **Email:** ${applicant.emailAddress}`);
                        if (applicant?.mobile) detailLines.push(`- **Mobile:** ${applicant.mobile}`);
                        if (appType) detailLines.push(`- **Application type:** ${appType}`);
                        detailLines.push(`- **Application:** ${isJoint ? "Joint" : "Single"}`);
                        if (applicant?.previouslyHaveMortgageLoan) detailLines.push(`- **First mortgage:** ${isFirstMortgage ? "Yes" : "No"}`);
                        if (mortgage?.propertyValueFormatted) detailLines.push(`- **Approx. home value:** ${mortgage.propertyValueFormatted}`);
                        if (mortgage?.mortgageAmountProposedFormatted) detailLines.push(`- **Mortgage amount:** ${mortgage.mortgageAmountProposedFormatted}`);
                        if (mortgage?.grossBasicIncomeFormatted) detailLines.push(`- **Gross income (p.a.):** ${mortgage.grossBasicIncomeFormatted}`);
                        if (hasVariableIncome) detailLines.push(`- **Variable income:** Yes (bonuses, overtime, etc.)`);
                        if (applicant?.monthlyRentAmount) detailLines.push(`- **Monthly rent/mortgage:** ${fmt(applicant.monthlyRentAmount)}`);

                        // Personalised greeting based on application context
                        let greeting = isJoint && secondApplicant?.firstName
                            ? `Hey ${firstName} and ${secondApplicant.firstName}, great to have you both here!`
                            : `Hey ${firstName}, great to have you here!`;

                        if (isFirstMortgage) {
                            greeting += ` Let's get you into your first home — starting with your **Approval in Principle** so you can house hunt with confidence and avoid delays.`;
                        } else if (appType?.toLowerCase().includes("switch")) {
                            greeting += ` Let's get you a better deal — starting with your **Approval in Principle** to keep things moving without delays.`;
                        } else {
                            greeting += ` Let's get you your **Approval in Principle** so you can house hunt with confidence and avoid delays.`;
                        }

                        greeting += `\n\nHere's what we know about you — could you confirm it's correct?\n\n`;

                        let greetingText = greeting;
                        if (detailLines.length > 0) {
                            greetingText += detailLines.join("\n");
                            greetingText += `\n\nDoes this look right?`;
                        } else {
                            greetingText += `We don't have much on file yet. Let's get started!\n\nWould you like to learn more about the process?`;
                        }

                        setOnboardingStep("details");

                        addMessage({
                            id: "1",
                            role: "agent",
                            text: greetingText,
                            type: "options",
                            options: detailLines.length > 0
                                ? [
                                    { label: "Yes, they look correct", value: "onboarding_details_yes" },
                                    { label: "No, I'll need to update them", value: "onboarding_details_no" },
                                ]
                                : [
                                    { label: "Yes, tell me more", value: "onboarding_learn_yes" },
                                    { label: "No, not yet!", value: "onboarding_learn_no" },
                                ],
                        });
                        } // end else (normal greeting)
                    } // end if (messages.length === 0)
                    setIsLoading(false);
                })
                .catch((err) => {
                    console.error("Failed to init session", err);
                    setIsLoading(false);
                    addMessage({
                        id: "1",
                        role: "agent",
                        text: "Hi there! I'm having trouble connecting to the server, but I'm here to help.",
                    });
                });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen, session]);

    const addMessage = (msg: Message) => {
        setMessages((prev) => [...prev, msg]);
    };

    // A document is "done" if it's Uploaded/Approved AND AI validation didn't fail
    const isDocDone = (d: PortalMortgageDocumentModel) =>
        (d.statusReason === 755980001 || d.statusReason === 755980003) && d.aiIsValid !== false;

    // Shows a typing indicator briefly, then replaces it with the real message
    const addAgentMessageWithDelay = (msg: Message, delay = 800) => {
        const loadingId = nextMsgId("_typing");
        addMessage({ id: loadingId, role: "agent", text: "..." });
        setTimeout(() => {
            setMessages((prev) => prev.filter((m) => m.id !== loadingId));
            addMessage(msg);
        }, delay);
    };

    // Wraps every message with the profile summary so the agent always has context
    const sendMessageWithContext = (
        message: string,
        applicationId: number,
        userName: string,
    ) => {
        const contextMessage = profileSummary
            ? `[Profile Context: ${profileSummary}]\n\n${message}`
            : message;
        return ChatWindowService.sendMessage(contextMessage, applicationId, userName);
    };

    // Builds a ProgressSummary from a docs array — used by inline progress and T4 resume
    const buildProgressSummaryFromDocs = (docs: PortalMortgageDocumentModel[]): ProgressSummary => {
        const primaryName = mortgage?.primaryApplicant?.fullName || userName;
        const secondaryName = mortgage?.secondaryApplicant?.fullName;
        const isJoint = !!(mortgage?.secondaryApplicant?.contactId);
        const activeDocs = docs.filter(d => d.aipCategory);

        const getDocStatus = (doc: PortalMortgageDocumentModel) => {
            // aiIsValid=false overrides status — document failed AI validation, needs re-upload
            if (doc.aiIsValid === false) return { status: "needs-action" as const, label: "Needs Re-upload" };
            if (doc.statusReason === 755980003) return { status: "approved" as const, label: "Approved" };
            if (doc.statusReason === 755980001) return { status: "uploaded" as const, label: "Uploaded" };
            if (doc.statusReason === 755980004) return { status: "in-progress" as const, label: "In Progress" };
            if (doc.statusReason === 755980002) return { status: "needs-action" as const, label: "Needs Action" };
            return { status: "needed" as const, label: "Needed" };
        };

        const buildApplicant = (name: string, applicableTo: number): ApplicantProgress => ({
            name,
            categories: aipCategories.map(cat => ({
                key: cat.key,
                label: cat.label,
                docs: activeDocs
                    .filter(d => d.applicableTo === applicableTo && d.aipCategory === cat.key)
                    .map(d => {
                        const s = getDocStatus(d);
                        return { name: d.portalName || d.document || cat.label, status: s.status, statusLabel: s.label };
                    }),
            })),
        });

        const applicants: ApplicantProgress[] = [buildApplicant(primaryName, 755980000)];
        if (isJoint && secondaryName) {
            applicants.push(buildApplicant(secondaryName, 755980001));
        }

        const doneDocs = activeDocs.filter(isDocDone);
        return {
            applicants,
            totalDocs: activeDocs.length,
            completedDocs: doneDocs.length,
            percentComplete: activeDocs.length > 0 ? Math.round((doneDocs.length / activeDocs.length) * 100) : 0,
        };
    };

    // Options that perform a side-effect but don't advance the conversation flow.
    // These should NOT disable sibling buttons and should NOT add a user message.
    const PEEK_OPTIONS = new Set(["show_progress", "book_call", "nav_documents"]);

    const handleOptionClick = async (value: string) => {
        // ── Peek actions: side-effect only, no message bubble, no disable ──
        if (PEEK_OPTIONS.has(value)) {
            if (value === "show_progress") {
                // Phase D: inject rich inline progress (implemented below)
                const loadingId = nextMsgId("progress_loading");
                addMessage({ id: loadingId, role: "agent", text: "..." });

                const progressData = buildProgressSummaryFromDocs(allDocs);

                let aiText = "";
                if (mortgage) {
                    try {
                        aiText = await ChatWindowService.summariseProfile("clientSummary", mortgage);
                    } catch {
                        const firstName = userName.split(" ")[0];
                        aiText = `Here's your document progress, ${firstName}.`;
                    }
                } else {
                    aiText = "Here's your document progress so far.";
                }

                setMessages(prev => prev.filter(m => m.id !== loadingId));
                addMessage({
                    id: nextMsgId("progress"),
                    role: "agent",
                    text: aiText,
                    progressSummary: progressData,
                    type: "options",
                    options: [
                        { label: "Continue uploading", value: "onboarding_upload_docs" },
                        { label: "Speak to your advisor", value: "book_call" },
                    ],
                });
            } else if (value === "book_call") {
                setShowBooking(true);
            } else if (value === "nav_documents") {
                navigate("/documents");
            }
            return;
        }

        // ── signup_newsletter: not a flow-advance, just an acknowledgement ──
        if (value === "signup_newsletter") {
            addAgentMessageWithDelay({
                id: nextMsgId("newsletter"),
                role: "agent",
                text: "Great — we'll keep you posted with the latest mortgage tips and updates!",
            });
            return;
        }

        // ── Flow-advancing options: track selection, add user message, disable siblings ──
        let optionLabel = value;
        for (let i = messages.length - 1; i >= 0; i--) {
            const found = messages[i].options?.find((o) => o.value === value);
            if (found) {
                optionLabel = found.label;
                setSelectedOptionsByMsg(prev => ({ ...prev, [messages[i].id]: value }));
                break;
            }
        }
        addMessage({ id: nextMsgId(), role: "user", text: optionLabel });

        if (!session) return;

        // ── Onboarding flow handlers ──

        if (value === "onboarding_details_yes_obsolete") {
            // Step 1 confirmed → move to Step 2 (buying timeframe)
            setOnboardingStep("timeline");
            addAgentMessageWithDelay({
                id: nextMsgId("timeline"),
                role: "agent",
                text: "Great! When are you looking to buy?",
                type: "options",
                options: [
                    { label: "< 3 months", value: "onboarding_timeframe_lt3" },
                    { label: "3-6 months", value: "onboarding_timeframe_3to6" },
                    { label: "6-12 months", value: "onboarding_timeframe_6to12" },
                    { label: "12+ months", value: "onboarding_timeframe_12plus" },
                ],
            });
            return;
        }

        if (value === "onboarding_details_no") {
            // Details not correct → offer to learn more
            addAgentMessageWithDelay({
                id: nextMsgId("learnmore"),
                role: "agent",
                text: "No problem. Would you like to learn more about the process?",
                type: "options",
                options: [
                    { label: "Yes, tell me more", value: "onboarding_learn_yes" },
                    { label: "Help me prepare", value: "prepare_plan" },
                    { label: "Sign Up For Newsletter", value: "signup_newsletter" },
                ],
            });
            return;
        }

        if  (value === "onboarding_details_yes") { //(value === "onboarding_timeframe_lt3" || value === "onboarding_timeframe_3to6") {
            // Ready soon → move to Step 3 (property in mind?)
            setOnboardingStep("property");
            addAgentMessageWithDelay({
                id: nextMsgId("property"),
                role: "agent",
                text: "Amazing! You said that you are looking to buy in the next 6 months, have you a property in mind yet?",
                type: "options",
                options: [
                    { label: "Yes", value: "onboarding_property_yes" },
                    { label: "No, not yet!", value: "onboarding_property_no"},

                    ]
            });
            return;
        }

        if (value === "onboarding_timeframe_6to12" || value === "onboarding_timeframe_12plus") {
            // Not ready yet → offer to learn more
            addAgentMessageWithDelay({
                id: nextMsgId("learnmore"),
                role: "agent",
                text: "No rush — plenty of time to get prepared. Would you like to learn more about the process?",
                type: "options",
                options: [
                    { label: "Yes, tell me more", value: "onboarding_learn_yes" },
                    { label: "Help me prepare", value: "prepare_plan" },
                    { label: "Sign Up For Newsletter", value: "signup_newsletter" },
                ],
            });
            return;
        }

        if (value === "onboarding_property_yes") {
            // Has property → AIP readiness, hand off to agent
            setOnboardingStep("done");
            addAgentMessageWithDelay({
                id: nextMsgId("aip"),
                role: "agent",
                text: "Let's get you your Approval in Principle! Here's what we'll need **for both applicants**:\n\n" +
                    "- **Proof of income** (Payslips, EDS, Salary Certificates)\n" +
                    "- **Open Banking/Bank statements** (last 6 months)\n\n\n" +
                    "For our ID Verification Partner (IDPal) you will need:\n" +
                    "- **Photo ID** (passport or driving licence)\n" +
                    "- **Proof of address** (utility bill or bank statement)\n\n" +
                    "Are you ready to get started with uploading your documents?",
                type: "options",
                options: [
                    { label: "Yes, let's go", value: "onboarding_upload_docs" },
                    { label: "Tell me what's involved", value: "onboarding_learn_yes" },
                    { label: "Speak to your advisor", value: "book_call" },
                ],
            });
            return;
        }

        if (value === "onboarding_start_uploading") {
            setOnboardingStep("done");
            const primaryName = mortgage?.primaryApplicant?.fullName || userName;
            const isJoint = !!(mortgage?.secondaryApplicant?.contactId);
            const secondaryName = mortgage?.secondaryApplicant?.fullName;

            const uploadOptions: { label: string; value: string }[] = [
                { label: `${primaryName}'s Payslips`, value: "upload_doc:payslips:primary" },
                { label: `${primaryName}'s Open Banking`, value: "upload_doc:banking:primary" },
            ];
            if (isJoint && secondaryName) {
                uploadOptions.push(
                    { label: `${secondaryName}'s Payslips`, value: "upload_doc:payslips:secondary" },
                    { label: `${secondaryName}'s Open Banking`, value: "upload_doc:banking:secondary" },
                );
            }
            uploadOptions.push({ label: "Speak to your advisor", value: "book_call" });

            addAgentMessageWithDelay({
                id: nextMsgId("aip"),
                role: "agent",
                text: "What would you like to start with?",
                type: "options",
                options: uploadOptions,
            });
            return;
        }

        if (value === "onboarding_upload_docs" || value === "guided_next" || value === "guided_skip") {
            setOnboardingStep("done");

            // On skip or next, advance the category index. On fresh start, reset to 0.
            let catIdx: number;
            if (value === "guided_skip" || value === "guided_next") {
                catIdx = guidedCategoryIndex + 1;
            } else {
                catIdx = 0;
            }
            setGuidedCategoryIndex(catIdx);

            // T1: Always reload fresh document list from the dedicated AIP endpoint
            let docs = allDocs;
            if (mortgage?.mortgageId) {
                try {
                    docs = await ChatWindowService.getAipDocuments(mortgage.mortgageId);
                    setAllDocs(docs);
                } catch { /* continue with cached list */ }
            }
            const primaryName = mortgage?.primaryApplicant?.fullName || userName;
            const secondaryName = mortgage?.secondaryApplicant?.fullName;
            const isJoint = !!(mortgage?.secondaryApplicant?.contactId);

            // Find the next category that has documents needing upload
            let foundCategory = false;
            while (catIdx < aipCategories.length) {
                const aipCat = aipCategories[catIdx];

                // T2: Use normalised aipCategory from backend instead of frontend matching
                const allForCategory = docs.filter((d) =>
                    d.aipCategory === aipCat.key,
                );

                // Split into done vs still needed (aiIsValid=false means not actually done)
                const alreadyDone = allForCategory.filter(isDocDone);
                const stillNeeded = allForCategory.filter(d => !isDocDone(d),
                );

                if (allForCategory.length > 0) {
                    // All uploaded/approved for this category — skip to next
                    if (stillNeeded.length === 0) {
                        // T6: Acknowledge the skipped category so the user knows it was recognised
                        addMessage({
                            id: nextMsgId("skip_ack"),
                            role: "agent",
                            text: `**${aipCat.label}** — already uploaded for ${isJoint ? "both applicants" : "you"}. Moving on.`,
                        });
                        catIdx++;
                        setGuidedCategoryIndex(catIdx);
                        continue;
                    }

                    // Some done, some needed — show what's done and prompt for the rest
                    const uploadOptions: { label: string; value: string }[] = [];
                    let statusText = `**Step ${catIdx + 1} of ${aipCategories.length}: ${aipCat.label}**\n\n`;

                    if (alreadyDone.length > 0) {
                        const doneNames = alreadyDone.map(d => {
                            const appName = d.applicableTo === 755980001 ? secondaryName || "Applicant 2" : primaryName;
                            return `${appName}'s ${d.portalName || d.document || aipCat.label}`;
                        }).join(", ");
                        statusText += `Already uploaded: ${doneNames}\n\n`;
                    }

                    statusText += `We still need:`;

                    for (const d of stillNeeded) {
                        const appType = d.applicableTo === 755980001 ? "secondary" : "primary";
                        const appName = d.applicableTo === 755980001
                            ? secondaryName || "Applicant 2"
                            : primaryName;
                        const contactId = d.applicableTo === 755980001
                            ? mortgage?.secondaryApplicant?.contactId || ""
                            : mortgage?.primaryApplicant?.contactId || "";
                        const docName = d.portalName || d.document || aipCat.label;
                        uploadOptions.push({
                            label: `Upload ${appName}'s ${docName}`,
                            value: `upload_real:${d.mortgageDocumentId}:${contactId}:${d.category || ""}:${appType}:${appName}:${docName}`,
                        });
                    }

                    uploadOptions.push({ label: "Skip for now", value: "guided_skip" });
                    uploadOptions.push({ label: "Speak to your advisor", value: "book_call" });

                    addAgentMessageWithDelay({
                        id: nextMsgId("guided"),
                        role: "agent",
                        text: statusText,
                        type: "options",
                        options: uploadOptions,
                    });
                    foundCategory = true;
                    setGuidedCategoryIndex(catIdx);
                    break;
                }
                catIdx++;
            }

            if (!foundCategory) {
                // All categories done
                addAgentMessageWithDelay({
                    id: nextMsgId("alldone"),
                    role: "agent",
                    text: "That's everything we need for now! Your advisor will review your documents and get back to you shortly.",
                    type: "options",
                    options: [
                        { label: "Go to Documents", value: "nav_documents" },
                        { label: "Speak to your advisor", value: "book_call" },
                    ],
                });
            }
            return;
        }

        if (value.startsWith("upload_real:")) {
            const parts = value.split(":");
            const mortgageDocumentId = parts[1];
            const contactId = parts[2];
            const category = parts[3];
            const applicantType = parts[4] as "primary" | "secondary" || "primary";
            const applicantName = parts[5] || "";
            const docName = parts.slice(6).join(":") || applicantName;

            console.log("ChatWindow upload_real:", { mortgageDocumentId, contactId, category, applicantType, applicantName, docName });

            setActivePill({
                uploadContext: {
                    docType: category?.toLowerCase().includes("banking") || category?.toLowerCase().includes("statement") ? "banking" : "payslips",
                    applicant: applicantType,
                    applicantName: applicantName,
                },
                mortgageDocumentId,
                contactId,
                category: category || "",
                name: docName,
            });
            return;
        }

        if (value === "onboarding_property_no") {
            // No property yet → offer to learn more
            addAgentMessageWithDelay({
                id: nextMsgId("learnmore"),
                role: "agent",
                text: "No problem. Would you like to learn more about the whole process?",
                type: "options",
                options: [
                    { label: "Yes, tell me more", value: "onboarding_learn_yes" },
                    { label: "No, not yet", value: "onboarding_learn_no" },
                ],
            });
            return;
        }

        if (value === "onboarding_learn_yes") {
            // Hand off to backend agent for process explanation
            setOnboardingStep("done");
            addMessage({ id: nextMsgId("load"), role: "agent", text: "..." });
            sendMessageWithContext("Tell me more about the mortgage process and what I need to do", session.applicationId, session.userName)
                .then((res) => handleAgentResponse(res));
            return;
        }

        if (value === "onboarding_learn_no") {
            // Done with onboarding → show standard menu
            setOnboardingStep("done");
            addAgentMessageWithDelay({
                id: nextMsgId("menu"),
                role: "agent",
                text: "No problem! I'm here whenever you need me. What would you like to do?",
                type: "options",
                options: [
                    { label: "Help me prepare", value: "prepare_plan" },
                    { label: "Sign Up For Newsletter", value: "signup_newsletter" },
                    { label: "Speak to your advisor", value: "book_call" },
                ],
            });
            return;
        }

        // ── Existing handlers ──

        if (value === "check_status") {
            addMessage({ id: nextMsgId("load"), role: "agent", text: "..." });
            // Show the status summary directly in chat, then also send it to the agent for context
            if (mortgage) {
                ChatWindowService.summariseProfile("statusUpdate", mortgage)
                    .then((summary) => {
                        setMessages((prev) => prev.filter((m) => !m.id.includes("load")));
                        addMessage({
                            id: nextMsgId("status"),
                            role: "agent",
                            text: summary,
                        });
                        // Also update the stored profile summary so subsequent messages have full context
                        setProfileSummary(summary);
                    })
                    .catch((err) => {
                        console.error("Failed to get status summary", err);
                        setMessages((prev) => prev.filter((m) => !m.id.includes("load")));
                        addMessage({
                            id: nextMsgId(),
                            role: "agent",
                            text: "Sorry, I couldn't retrieve your status right now.",
                        });
                    });
            } else {
                setMessages((prev) => prev.filter((m) => !m.id.includes("load")));
                addMessage({
                    id: nextMsgId(),
                    role: "agent",
                    text: "I don't have your mortgage details loaded yet. Please try again in a moment.",
                });
            }
        } else if (value === "learn_more") {
            addMessage({ id: nextMsgId("load"), role: "agent", text: "..." });
            sendMessageWithContext("Tell me more about Doddl and mortgages", session.applicationId, session.userName)
                .then((res) => handleAgentResponse(res));
        } else if (value === "prepare_plan") {
            addMessage({ id: nextMsgId("load"), role: "agent", text: "..." });
            sendMessageWithContext("Help me prepare for a mortgage", session.applicationId, session.userName)
                .then((res) => handleAgentResponse(res));
        } else if (value === "start_application") {
            addMessage({ id: nextMsgId("load"), role: "agent", text: "..." });
            sendMessageWithContext("I want to start my application.", session.applicationId, session.userName)
                .then((res) => handleAgentResponse(res));
        } else if (value === "upload_yes" || value === "upload_more") {
            addAgentMessageWithDelay({
                id: nextMsgId(),
                role: "agent",
                text: "Great! Please upload your document below.",
                type: "upload_prompt",
            });
        } else if (value.startsWith("upload_doc:")) {
            const [, docType, applicant] = value.split(":");
            const applicantName = applicant === "secondary"
                ? mortgage?.secondaryApplicant?.fullName || "Second Applicant"
                : mortgage?.primaryApplicant?.fullName || userName;
            const docLabel = docType === "payslips" ? "payslips" : "open banking statement";
            const uploadCtx: UploadContext = {
                docType: docType as "payslips" | "banking",
                applicant: applicant as "primary" | "secondary",
                applicantName,
            };
            addAgentMessageWithDelay({
                id: nextMsgId(),
                role: "agent",
                text: `Great! Please upload ${applicantName}'s ${docLabel} below.`,
                type: "upload_prompt",
                uploadLabel: `Click to upload ${applicantName}'s ${docLabel}`,
                uploadContext: uploadCtx,
            });
        } else if (value === "show_matches") {
            navigate("/property");
        } else if (value === "start_app_confirmed") {
            sendMessageWithContext("I want to start my application.", session.applicationId, session.userName)
                .then((res) => handleAgentResponse(res));
        } else if (value === "confirm_income") {
            addMessage({ id: nextMsgId("load"), role: "agent", text: "..." });
            sendMessageWithContext("The user has confirmed all income sources are accounted for.", session.applicationId, session.userName)
                .then((res) => handleAgentResponse(res));
        }
    };

    const handleAgentResponse = (res: ChatAgentResponse) => {
        setMessages((prev) => prev.filter((m) => !m.id.includes("load")));

        if (res.currentAgentState) {
            setSession((prev) =>
                prev ? { ...prev, agentState: res.currentAgentState } : null,
            );
        }

        let messageText = res.message;
        let timelineData: TimelineItem[] | undefined;

        // Extract timeline data — strip the raw block from display even if parsing fails
        if (messageText.includes("[[TIMELINE:")) {
            const start = messageText.indexOf("[[TIMELINE:");
            // Find the matching closing ]] after the opening tag
            const searchFrom = start + 11;
            const end = messageText.indexOf("]]", searchFrom);
            const textBefore = messageText.substring(0, start).trim();
            const textAfter = end > start ? messageText.substring(end + 2).trim() : "";

            if (end > start) {
                try {
                    const json = messageText.substring(searchFrom, end).trim();
                    timelineData = JSON.parse(json);
                } catch (e) {
                    console.error("Failed to parse timeline JSON", e);
                }
            }

            // Always strip the [[TIMELINE:...]] block from displayed text
            messageText = [textBefore, textAfter].filter(Boolean).join("\n\n");
        }

        addMessage({
            id: nextMsgId("res"),
            role: "agent",
            text: messageText,
            timeline: timelineData,
        });

        // Profile snapshot — log only for now (backend persists to Dataverse)
        if (res.profileSnapshot && Object.keys(res.profileSnapshot).length > 0) {
            console.log("ChatWindow: agent returned profileSnapshot", res.profileSnapshot);
        }

        // Surface conflicts
        if (res.conflicts && res.conflicts.length > 0) {
            setActiveConflicts(res.conflicts);
        }

        // Handle suggested actions
        if (
            res.suggestedActions?.includes("upload_more") ||
            res.suggestedActions?.includes("upload_first")
        ) {
            // Only show the generic upload prompt when NOT in the guided flow.
            // In the guided flow, the post-analysis logic provides specific upload_real: buttons.
            if (allDocs.length === 0) {
                setTimeout(() => {
                    addMessage({
                        id: nextMsgId("pmt"),
                        role: "agent",
                        text: "You can upload your documents below.",
                        type: "upload_prompt",
                    });
                }, 1000);
            }
        } else if (res.suggestedActions?.includes("start_application")) {
            setTimeout(() => {
                addMessage({
                    id: nextMsgId("start"),
                    role: "agent",
                    text: "Ready to start the full application?",
                    type: "options",
                    options: [
                        { label: "Yes, start application", value: "start_app_confirmed" },
                    ],
                });
            }, 1000);
        } else if (res.suggestedActions?.includes("confirm_income")) {
            setTimeout(() => {
                addMessage({
                    id: nextMsgId("cnf"),
                    role: "agent",
                    text: "Is there any other income we need to include?",
                    type: "options",
                    options: [
                        { label: "That's all the income", value: "confirm_income" },
                        { label: "Upload more docs", value: "upload_more" },
                    ],
                });
            }, 800);
        }
    };

    const handlePinSubmit = async () => {
        if (!pendingPin || !inputValue.trim() || !session || !mortgage?.mortgageId) return;

        const pin = inputValue;
        setInputValue("");
        // Show masked PIN in chat
        addMessage({ id: nextMsgId(), role: "user", text: "••••••••" });

        addMessage({ id: nextMsgId("unlock"), role: "agent", text: "..." });

        try {
            await ChatWindowService.updateCommentPin(
                mortgage.mortgageId,
                pendingPin.fileName,
                pendingPin.uploadId,
                pin,
                pendingPin.mortgageDocumentId,
            );

            // PIN accepted — mark as uploaded and analyse
            setMessages((prev) => prev.filter((m) => !m.id.includes("unlock")));
            addMessage({
                id: nextMsgId("unlocked"),
                role: "agent",
                text: "Document unlocked successfully!",
            });

            try {
                if (pendingPin.mortgageDocumentId) {
                    await ChatWindowService.markDocumentUploaded(pendingPin.mortgageDocumentId, mortgage.mortgageId);
                }
            } catch (err) {
                console.warn("ChatWindow: markDocumentUploaded failed after PIN", err);
            }

            const ctx = pendingPin.uploadContext;
            const fileUrl = pendingPin.fileUrl;
            const pinCategory = pendingPin.category;
            const pinPillContext = pendingPin.mortgageDocumentId ? {
                mortgageDocumentId: pendingPin.mortgageDocumentId,
                contactId: pendingPin.contactId || "",
                category: pinCategory || "",
                name: pendingPin.pillName || "",
            } : undefined;
            setPendingPin(null);
            // T3: Pass the category from the original upload context
            const pinAnalysisCategory = pinCategory?.toLowerCase().includes("income") || pinCategory?.toLowerCase().includes("employment") ? "Income"
                : pinCategory?.toLowerCase().includes("banking") ? "Banking"
                : pinCategory?.toLowerCase().includes("identity") || pinCategory?.toLowerCase().includes("verification") ? "Identity"
                : undefined;
            await analyseUploadedDocument(fileUrl, ctx, pinAnalysisCategory, pinPillContext);
        } catch (err) {
            console.error("PIN unlock failed", err);
            setMessages((prev) => prev.filter((m) => !m.id.includes("unlock")));
            addMessage({
                id: nextMsgId("pinfail"),
                role: "agent",
                text: "That password didn't work. Could you try again?",
            });
        }
    };

    const handleTextSubmit = async (e?: React.FormEvent) => {
        e?.preventDefault();
        if (!inputValue.trim()) return;

        // If waiting for a PIN, handle that instead of normal message flow
        if (pendingPin) {
            await handlePinSubmit();
            return;
        }

        const val = inputValue;
        setInputValue("");
        addMessage({ id: nextMsgId(), role: "user", text: val });

        if (!session) return;

        addMessage({
            id: nextMsgId("loading"),
            role: "agent",
            text: "...",
        });

        try {
            const res = await sendMessageWithContext(
                val,
                session.applicationId,
                session.userName,
            );
            handleAgentResponse(res);
        } catch (err) {
            setMessages((prev) => prev.filter((m) => !m.id.includes("loading")));
            addMessage({
                id: nextMsgId(),
                role: "agent",
                text: "I'm having trouble connecting.",
            });
        }
    };

    const resolveDocumentRecord = (ctx: UploadContext) => {
        // Match the upload context to a mortgage document record from Dataverse
        const contactId = ctx.applicant === "secondary"
            ? mortgage?.secondaryApplicant?.contactId
            : mortgage?.primaryApplicant?.contactId;
        const applicableTo = ctx.applicant === "secondary" ? 755980001 : 755980000;

        const docs = userCtx?.mortgageDocumentList ?? [];

        // Find the matching document record — try multiple matching strategies
        let doc = docs.find((d) => {
            if (d.applicableTo !== applicableTo) return false;
            if (ctx.docType === "payslips") {
                const cat = (d.category || "").toLowerCase();
                const code = (d.categoryUniqueCode || "").toLowerCase();
                return cat.includes("income") || cat.includes("employment") || cat.includes("payslip")
                    || code.includes("payslip") || code.includes("eds") || code.includes("salary");
            }
            return d.categoryUniqueCode === "STATEMENT";
        });

        // If no specific match, try to find ANY document for this applicant that requires file upload
        if (!doc && docs.length > 0) {
            doc = docs.find((d) =>
                d.applicableTo === applicableTo &&
                d.fileUploadRequired !== false &&
                d.statusReason !== 755980003, // not already Approved
            );
        }

        return {
            contactId: contactId || "",
            category: doc?.category || (ctx.docType === "payslips" ? "Income" : "Banking"),
            mortgageDocumentId: doc?.mortgageDocumentId || "",
        };
    };

    // Called after a file is uploaded to SharePoint — handles accepted, encrypted, and closed cases
    const handlePillDone = async (uploadResult: { url: string; accepted: boolean; message: string | null; uploadId?: string; docattributes?: DocAttributes | null } | null) => {
        if (!activePill || !session) return;
        const ctx = activePill.uploadContext;
        const mortgageDocId = activePill.mortgageDocumentId;
        const pillContactId = activePill.contactId;
        const pillCategory = activePill.category;
        const pillName = activePill.name;
        setActivePill(null);

        const docLabel = ctx.docType === "payslips" ? "payslips" : "open banking statement";

        // User closed modal without uploading
        if (!uploadResult) {
            addMessage({
                id: nextMsgId("closed"),
                role: "agent",
                text: "No worries — you can upload that whenever you're ready. What would you like to do next?",
            });
            return;
        }

        const spFileName = uploadResult.url ? decodeURIComponent(uploadResult.url.split("/").pop() || "") : "";
        addMessage({
            id: nextMsgId(),
            role: "user",
            text: `Uploaded: ${spFileName || `${ctx.applicantName}'s ${docLabel}`}`,
            type: "file_preview",
        });

        // Encrypted PDF — prompt for password
        if (!uploadResult.accepted) {
            const fileName = uploadResult.url ? uploadResult.url.split("/").pop() || "document" : "document";
            setPendingPin({
                fileUrl: uploadResult.url,
                fileName,
                uploadId: uploadResult.uploadId || "",
                mortgageDocumentId: mortgageDocId,
                uploadContext: ctx,
                category: pillCategory,
                contactId: pillContactId,
                pillName: pillName,
            });
            addAgentMessageWithDelay({
                id: nextMsgId("pin"),
                role: "agent",
                text: `This document appears to be **password-protected**. Please type the password below so we can unlock it.`,
            });
            return;
        }

        // Show AI validation feedback from the SP upload service if available
        const attrs = uploadResult.docattributes;
        if (attrs) {
            if (!attrs.aiIsValid) {
                // Document failed AI validation — do NOT continue with analysis
                addMessage({
                    id: nextMsgId("aival"),
                    role: "agent",
                    text: `**Document Validation Issue**\n\n${attrs.aiCustomerComments || attrs.aiComments || "There was an issue with this document."}`
                        + (attrs.aiRationale ? `\n\n*${attrs.aiRationale}*` : ""),
                    type: "options",
                    options: [
                        { label: "Try uploading again", value: `upload_real:${mortgageDocId}:${pillContactId}:${pillCategory}:${ctx.applicant}:${ctx.applicantName}:${pillName}` },
                        { label: "Skip for now", value: "guided_skip" },
                        { label: "Speak to your advisor", value: "book_call" },
                    ],
                });
                return;
            }

            if (attrs.aiCustomerComments) {
                addMessage({
                    id: nextMsgId("aival"),
                    role: "agent",
                    text: attrs.aiCustomerComments,
                });
            }
        }

        // File accepted — mark as uploaded in Dataverse, then analyse
        try {
            if (mortgageDocId && mortgage?.mortgageId) {
                await ChatWindowService.markDocumentUploaded(mortgageDocId, mortgage.mortgageId);
            }
        } catch (err) {
            console.warn("ChatWindow: markDocumentUploaded failed", err);
        }

        // T3: Pass the document category so the backend applies correct extraction rules
        const analysisCategory = pillCategory?.toLowerCase().includes("income") || pillCategory?.toLowerCase().includes("employment") ? "Income"
            : pillCategory?.toLowerCase().includes("banking") ? "Banking"
            : pillCategory?.toLowerCase().includes("identity") || pillCategory?.toLowerCase().includes("verification") ? "Identity"
            : undefined;
        await analyseUploadedDocument(uploadResult.url, ctx, analysisCategory, {
            mortgageDocumentId: mortgageDocId,
            contactId: pillContactId,
            category: pillCategory,
            name: pillName,
        });
    };

    // Shared analysis flow — called after accepted upload or successful PIN unlock
    const analyseUploadedDocument = async (
        fileUrl: string,
        ctx: UploadContext,
        category?: string,
        pill?: { mortgageDocumentId: string; contactId: string; category: string; name: string },
    ) => {
        if (!session) return;

        addMessage({
            id: nextMsgId("prog"),
            role: "agent",
            text: "Analyzing...",
        });

        let analysisRes: ChatAgentResponse | null = null;
        try {
            // Get the correct applicant's CRM profile
            const applicantProfile = ctx.applicant === "secondary"
                ? mortgage?.secondaryApplicant
                : mortgage?.primaryApplicant;

            // T3: Pass document category so the backend uses correct extraction rules
            analysisRes = await ChatWindowService.analyseDocument(
                fileUrl,
                session.applicationId,
                session.userName,
                ctx.applicantName,
                ctx.applicant,
                applicantProfile || undefined,
                category,
            );
            setMessages((prev) => prev.filter((m) => !m.id.includes("prog")));
            handleAgentResponse(analysisRes);
        } catch (err) {
            console.error(err);
            setMessages((prev) => prev.filter((m) => !m.id.includes("prog")));
            addMessage({
                id: nextMsgId("ok"),
                role: "agent",
                text: `Your document has been uploaded successfully. Your advisor will review it shortly.`,
            });
        }

        // If correlation found conflicts, offer re-upload instead of continuing the guided flow
        if (analysisRes?.conflicts && analysisRes.conflicts.length > 0 && pill) {
            const issueList = analysisRes.conflicts.map(c =>
                `${c.field}: **${c.newValue}** (document) vs **${c.existingValue}** (on file)`
            ).join("\n");

            const reuploadValue = `upload_real:${pill.mortgageDocumentId}:${pill.contactId}:${pill.category}:${ctx.applicant}:${ctx.applicantName}:${pill.name}`;

            addMessage({
                id: nextMsgId("conflict_action"),
                role: "agent",
                text: `We noticed a few things that don't quite match your application:\n\n${issueList}\n\nWould you like to try a different document?`,
                type: "options",
                options: [
                    { label: "Upload a different document", value: reuploadValue },
                    { label: "Continue anyway", value: "guided_next" },
                    { label: "Speak to your advisor", value: "book_call" },
                ],
            });
            return; // Skip normal next-step logic
        }

        // T1: Reload documents from dedicated AIP endpoint to reflect the upload
        let latestDocs = allDocs;
        if (mortgage?.mortgageId) {
            try {
                latestDocs = await ChatWindowService.getAipDocuments(mortgage.mortgageId);
                setAllDocs(latestDocs);
            } catch { /* continue with existing list */ }
        }

        setTimeout(() => {
            // Check what's still pending in the current AIP category
            const currentCat = aipCategories[guidedCategoryIndex];
            const primaryName = mortgage?.primaryApplicant?.fullName || userName;
            const secondaryName = mortgage?.secondaryApplicant?.fullName;

            // T2: Use aipCategory for matching (aiIsValid=false counts as still pending)
            const stillPending = currentCat ? latestDocs.filter((d) =>
                d.aipCategory === currentCat.key && !isDocDone(d)
            ) : [];

            const options: { label: string; value: string }[] = [];

            if (stillPending.length > 0) {
                // Still have docs to upload in this category (e.g. Applicant 2's payslip)
                for (const d of stillPending) {
                    const appType = d.applicableTo === 755980001 ? "secondary" : "primary";
                    const appName = d.applicableTo === 755980001
                        ? secondaryName || "Applicant 2" : primaryName;
                    const contactId = d.applicableTo === 755980001
                        ? mortgage?.secondaryApplicant?.contactId || ""
                        : mortgage?.primaryApplicant?.contactId || "";
                    const docName = d.portalName || d.document || currentCat.label;
                    options.push({
                        label: `Upload ${appName}'s ${docName}`,
                        value: `upload_real:${d.mortgageDocumentId}:${contactId}:${d.category || ""}:${appType}:${appName}:${docName}`,
                    });
                }
                options.push({ label: "Skip and move to next step", value: "guided_next" });

                addMessage({
                    id: nextMsgId("moredocs"),
                    role: "agent",
                    text: `We still need the following **${currentCat.label.toLowerCase()}**:`,
                    type: "options",
                    options,
                });
            } else {
                // Current category is complete — move to next
                addMessage({
                    id: nextMsgId("catdone"),
                    role: "agent",
                    text: `Great — all **${currentCat?.label.toLowerCase() || "documents"}** are uploaded! Let's move on.`,
                });
                // Trigger next category after a short delay
                setTimeout(() => handleOptionClick("guided_next"), 800);
            }
        }, 1000);
    };

    // Fallback file upload handler for plain file input (no mortgage context)
    const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file || !session) return;

        addMessage({
            id: nextMsgId(),
            role: "user",
            text: `Uploaded: ${file.name}`,
            type: "file_preview",
            file,
        });

        addMessage({
            id: nextMsgId("prog"),
            role: "agent",
            text: "Analyzing...",
        });

        try {
            const res: ChatAgentResponse = await ChatWindowService.uploadDocument(
                file,
                session.applicationId,
                session.userName,
            );
            setMessages((prev) => prev.filter((m) => !m.id.includes("prog")));
            handleAgentResponse(res);
        } catch (err) {
            console.error(err);
            setMessages((prev) => prev.filter((m) => !m.id.includes("prog")));
            addMessage({
                id: nextMsgId("err"),
                role: "agent",
                text: "Sorry, something went wrong processing that file.",
            });
        }
    };

    const handleReset = () => {
        setMessages([]);
        setSession(null);
        setInputValue("");
        setInputState("text");
        setActiveConflicts([]);
        setOnboardingStep(null);
        setIsLoading(false);
        setPendingPin(null);
        setActivePill(null);
        setGuidedCategoryIndex(0);
        setAllDocs([]);
        setSelectedOptionsByMsg({});
    };

    // ── FAB (closed state) — hidden when embedded ──
    if (!isOpen && !embedded && mortgage?.isAIAgentEnabled) {
        return (
            <button onClick={() => setIsOpen(true)} className="chatwindow-fab">
                <MessageCircle className="chatwindow-fab__icon" />
                <span className="chatwindow-fab__ping">
                    <span className="chatwindow-fab__ping-wave" />
                    <span className="chatwindow-fab__ping-dot" />
                </span>
            </button>
        );
    }

    if (!isOpen) return null;

    // ── Chat Panel (open state) ──
    return (
       
        <div className={`chatwindow-panel ${embedded ? "chatwindow-panel--embedded" : isExpanded ? "chatwindow-panel--expanded" : "chatwindow-panel--collapsed"}`}>
            {/* Header */}
            <div className="chatwindow-header">
                <div className="chatwindow-header__left">
                    <div className="chatwindow-header__icon-wrap">
                        <MessageCircle />
                        <span className="chatwindow-header__online">
                            <span className="chatwindow-header__online-wave" />
                            <span className="chatwindow-header__online-dot" />
                        </span>
                    </div>
                    <div>
                        <h3 className="chatwindow-header__title">
                            Doddl Assistant{" "}
                            {session?.agentState ? `(${session.agentState})` : ""}
                        </h3>
                        <p className="chatwindow-header__subtitle">
                            {session?.agentState === "BROKER"
                                ? "Processing Application"
                                : "Always here to help"}
                        </p>
                    </div>
                </div>
                <div className="chatwindow-header__actions">
                    {allDocs.length > 0 && (
                        <button
                            onClick={() => setShowProgress(!showProgress)}
                            className={`chatwindow-header__btn ${showProgress ? "chatwindow-header__btn--active" : ""}`}
                            title="Document Progress"
                        >
                            <ClipboardCheck />
                        </button>
                    )}
                    <button onClick={handleReset} className="chatwindow-header__btn" title="Start New Chat">
                        <RotateCcw />
                    </button>
                    {!embedded && (
                        <button onClick={() => setIsExpanded(!isExpanded)} className="chatwindow-header__btn" title={isExpanded ? "Collapse" : "Expand"}>
                            {isExpanded ? <Minimize2 /> : <Maximize2 />}
                        </button>
                    )}
                    {!embedded && (
                        <button onClick={() => setIsOpen(false)} className="chatwindow-header__btn" title="Close">
                            <X />
                        </button>
                    )}
                </div>
            </div>

            {/* AIP Document Progress Panel */}
            {/* T7: Improved progress panel with NeedsAction/InProgress states and empty-category handling */}
            {showProgress && allDocs.length > 0 && (() => {
                const primaryName = mortgage?.primaryApplicant?.fullName || userName;
                const secondaryName = mortgage?.secondaryApplicant?.fullName;
                const isJoint = !!(mortgage?.secondaryApplicant?.contactId);
                // Only count AIP-categorised docs
                const activeDocs = allDocs.filter(d => d.aipCategory);
                const doneDocs = activeDocs.filter(isDocDone);
                const pct = activeDocs.length > 0 ? Math.round((doneDocs.length / activeDocs.length) * 100) : 0;

                const getStatus = (doc: PortalMortgageDocumentModel) => {
                    if (doc.statusReason === 755980003) return "approved";
                    if (doc.statusReason === 755980001) return "uploaded";
                    if (doc.statusReason === 755980004) return "in-progress";
                    if (doc.statusReason === 755980002) return "needs-action";
                    return "needed";
                };

                const getStatusLabel = (status: string) => {
                    switch (status) {
                        case "approved": return "Approved";
                        case "uploaded": return "Uploaded";
                        case "in-progress": return "In Progress";
                        case "needs-action": return "Needs Action";
                        default: return "Needed";
                    }
                };

                const renderApplicant = (name: string, applicableTo: number) => {
                    // T7: Show ALL AIP categories, even if no docs exist for them
                    const applicantGroups = aipCategories.map(cat => {
                        const docs = activeDocs.filter(d =>
                            d.applicableTo === applicableTo &&
                            d.aipCategory === cat.key
                        );
                        return { cat, docs };
                    });

                    return (
                        <div className="chatwindow-progress__card" key={`progress-${applicableTo}`}>
                            <div className="chatwindow-progress__card-name">{name}</div>
                            {applicantGroups.map(({ cat, docs }) => (
                                <div key={cat.key} className="chatwindow-progress__category">
                                    <div className="chatwindow-progress__cat-label">{cat.label}</div>
                                    {docs.length === 0 ? (
                                        <div className="chatwindow-progress__row chatwindow-progress__row--empty">
                                            <Circle size={10} style={{ opacity: 0.4 }} />
                                            <span className="chatwindow-progress__doc-name" style={{ fontStyle: "italic", opacity: 0.6 }}>Not set up — contact your advisor</span>
                                        </div>
                                    ) : docs.map(d => {
                                        const status = getStatus(d);
                                        return (
                                            <div key={d.mortgageDocumentId} className={`chatwindow-progress__row chatwindow-progress__row--${status}`}>
                                                <div className={`chatwindow-progress__dot chatwindow-progress__dot--${status}`}>
                                                    {(status === "approved" || status === "uploaded") && <Check size={10} />}
                                                </div>
                                                <span className="chatwindow-progress__doc-name">{d.portalName || d.document || cat.label}</span>
                                                <span className={`chatwindow-progress__chip chatwindow-progress__chip--${status}`}>
                                                    {getStatusLabel(status)}
                                                </span>
                                            </div>
                                        );
                                    })}
                                </div>
                            ))}
                        </div>
                    );
                };

                return (
                    <div className="chatwindow-progress">
                        <div className="chatwindow-progress__header">
                            <strong>AIP Document Checklist</strong>
                            <button className="chatwindow-progress__close" onClick={() => setShowProgress(false)}>
                                <X size={14} />
                            </button>
                        </div>
                        <div className="chatwindow-progress__bar-wrap">
                            <div className="chatwindow-progress__bar">
                                <div className="chatwindow-progress__bar-fill" style={{ width: `${pct}%` }} />
                            </div>
                            <span className="chatwindow-progress__bar-label">{doneDocs.length} of {activeDocs.length} — {pct}%</span>
                        </div>
                        {renderApplicant(primaryName, 755980000)}
                        {isJoint && secondaryName && renderApplicant(secondaryName, 755980001)}
                    </div>
                );
            })()}

            {/* Messages */}
            <div className="chatwindow-messages">
                {/* Loading State */}
                {isLoading && messages.length === 0 && (
                    <div className="chatwindow-loading">
                        <div className="chatwindow-loading__icon">
                            <MessageCircle />
                        </div>
                        <p className="chatwindow-loading__title">Getting things ready for you...</p>
                        <div className="chatwindow-loading__dots">
                            <div className="chatwindow-loading__dot" />
                            <div className="chatwindow-loading__dot" />
                            <div className="chatwindow-loading__dot" />
                        </div>
                    </div>
                )}

                {/* Conflict Banner */}
                {activeConflicts.length > 0 && (
                    <div className="chatwindow-conflict">
                        <div className="chatwindow-conflict__header">
                            <AlertTriangle />
                            <span className="chatwindow-conflict__title">Data Conflict Detected</span>
                        </div>
                        {activeConflicts.map((c) => (
                            <div key={c.field} className="chatwindow-conflict__field">
                                <strong>{c.field}</strong>: &ldquo;{c.existingValue}&rdquo; ({c.existingSource}) vs &ldquo;{c.newValue}&rdquo; ({c.newSource})
                            </div>
                        ))}
                        <p className="chatwindow-conflict__hint">The agent will ask you to confirm the correct value.</p>
                    </div>
                )}

                {messages.map((msg) => (
                    <div
                        key={msg.id}
                        className={`chatwindow-msg ${msg.role === "user" ? "chatwindow-msg--user" : "chatwindow-msg--agent"}`}
                    >
                        <div className={`chatwindow-msg__bubble ${msg.role === "user" ? "chatwindow-msg__bubble--user" : "chatwindow-msg__bubble--agent"}`}>
                            <div className="chatwindow-md">
                                {msg.text === "..." ? (
                                    <TypingIndicator />
                                ) : msg.text === "Analyzing..." ? (
                                    <TypingIndicator label="Analyzing your document" />
                                ) : (
                                    <ReactMarkdown
                                        remarkPlugins={[remarkGfm]}
                                        components={{
                                            h1: ({ node, ...props }: any) => <h1 className="chatwindow-md-h1" {...props} />,
                                            h2: ({ node, ...props }: any) => <h2 className="chatwindow-md-h2" {...props} />,
                                            h3: ({ node, ...props }: any) => <h3 className="chatwindow-md-h3" {...props} />,
                                            p: ({ node, ...props }: any) => <p className="chatwindow-md-p" {...props} />,
                                            ul: ({ node, ...props }: any) => <ul className="chatwindow-md-ul" {...props} />,
                                            ol: ({ node, ...props }: any) => <ol className="chatwindow-md-ol" {...props} />,
                                            li: ({ node, ...props }: any) => <li className="chatwindow-md-li" {...props} />,
                                            a: ({ node, ...props }: any) => (
                                                <a className="chatwindow-md-a" target="_blank" rel="noopener noreferrer" {...props} />
                                            ),
                                            strong: ({ node, ...props }: any) => <strong className="chatwindow-md-strong" {...props} />,
                                            table: ({ node, ...props }: any) => (
                                                <div className="chatwindow-md-table-wrap">
                                                    <table className="chatwindow-md-table" {...props} />
                                                </div>
                                            ),
                                            thead: ({ node, ...props }: any) => <thead className="chatwindow-md-thead" {...props} />,
                                            th: ({ node, ...props }: any) => <th className="chatwindow-md-th" {...props} />,
                                            td: ({ node, ...props }: any) => <td className="chatwindow-md-td" {...props} />,
                                        }}
                                    >
                                        {msg.text}
                                    </ReactMarkdown>
                                )}
                            </div>

                            {/* Option Buttons — T5: disable after selection */}
                            {msg.type === "options" && msg.options && (() => {
                                const selectedValue = selectedOptionsByMsg[msg.id];
                                return (
                                    <div className="chatwindow-options">
                                        {msg.options.map((opt) => {
                                            const isSelected = selectedValue === opt.value;
                                            const isDisabled = selectedValue !== undefined && !isSelected;
                                            return (
                                                <button
                                                    key={opt.value}
                                                    onClick={() => handleOptionClick(opt.value)}
                                                    className={`chatwindow-option-btn${isSelected ? " chatwindow-option-btn--selected" : ""}${isDisabled ? " chatwindow-option-btn--disabled" : ""}`}
                                                    disabled={isDisabled}
                                                >
                                                    {opt.label}
                                                </button>
                                            );
                                        })}
                                    </div>
                                );
                            })()}


                            {/* Timeline */}
                            {msg.timeline && (
                                <div className="chatwindow-timeline">
                                    {msg.timeline.map((item, idx) => (
                                        <div
                                            key={idx}
                                            className="chatwindow-timeline__item"
                                            style={{ animationDelay: `${idx * 150}ms` }}
                                        >
                                            <div className="chatwindow-timeline__dot-wrap">
                                                <div className={`chatwindow-timeline__dot ${idx === 0 ? "chatwindow-timeline__dot--active" : ""}`} />
                                            </div>
                                            <h4 className="chatwindow-timeline__title">{item.title}</h4>
                                            <p className="chatwindow-timeline__desc">{item.desc}</p>
                                        </div>
                                    ))}
                                </div>
                            )}

                            {/* Inline Progress Summary */}
                            {msg.progressSummary && (
                                <div className="chatwindow-inline-progress">
                                    <div className="chatwindow-inline-progress__bar-wrap">
                                        <div className="chatwindow-inline-progress__bar">
                                            <div className="chatwindow-inline-progress__bar-fill" style={{ width: `${msg.progressSummary.percentComplete}%` }} />
                                        </div>
                                        <span className="chatwindow-inline-progress__bar-label">
                                            {msg.progressSummary.completedDocs} of {msg.progressSummary.totalDocs} — {msg.progressSummary.percentComplete}%
                                        </span>
                                    </div>
                                    {msg.progressSummary.applicants.map((applicant) => (
                                        <div key={applicant.name} className="chatwindow-inline-progress__applicant">
                                            <div className="chatwindow-inline-progress__applicant-name">{applicant.name}</div>
                                            {applicant.categories.map((cat) => (
                                                <div key={cat.key} className="chatwindow-inline-progress__category">
                                                    <div className="chatwindow-inline-progress__cat-label">{cat.label}</div>
                                                    {cat.docs.length === 0 ? (
                                                        <div className="chatwindow-inline-progress__row chatwindow-inline-progress__row--empty">
                                                            <Circle size={10} style={{ opacity: 0.4 }} />
                                                            <span className="chatwindow-inline-progress__doc-name" style={{ fontStyle: "italic", opacity: 0.6 }}>Not set up yet</span>
                                                        </div>
                                                    ) : cat.docs.map((doc, idx) => (
                                                        <div key={idx} className={`chatwindow-inline-progress__row chatwindow-inline-progress__row--${doc.status}`}>
                                                            <div className={`chatwindow-inline-progress__dot chatwindow-inline-progress__dot--${doc.status}`}>
                                                                {(doc.status === "approved" || doc.status === "uploaded") && <Check size={10} />}
                                                            </div>
                                                            <span className="chatwindow-inline-progress__doc-name">{doc.name}</span>
                                                            <span className={`chatwindow-inline-progress__chip chatwindow-inline-progress__chip--${doc.status}`}>
                                                                {doc.statusLabel}
                                                            </span>
                                                        </div>
                                                    ))}
                                                </div>
                                            ))}
                                        </div>
                                    ))}
                                </div>
                            )}

                            {/* External Link */}
                            {msg.link && (
                                <div className="chatwindow-link">
                                    <a href={msg.link.url} target="_blank" rel="noopener noreferrer">
                                        <span className="chatwindow-link__label">{msg.link.label}</span>
                                        <span className="chatwindow-link__arrow">&rarr;</span>
                                    </a>
                                </div>
                            )}

                            {/* Upload Prompt */}
                            {msg.type === "upload_prompt" && (
                                <div className="chatwindow-upload">
                                    {msg.uploadContext && mortgage?.mortgageId ? (
                                        <button
                                            className="chatwindow-option-btn"
                                            onClick={() => {
                                                const resolved = resolveDocumentRecord(msg.uploadContext!);
                                                if (!resolved.mortgageDocumentId) {
                                                    addMessage({
                                                        id: nextMsgId("nodoc"),
                                                        role: "agent",
                                                        text: "We don't have a matching document folder set up for this yet. Please use the **Documents** page to upload, or speak to your advisor.",
                                                        type: "options",
                                                        options: [
                                                            { label: "Go to Documents", value: "nav_documents" },
                                                            { label: "Speak to your advisor", value: "book_call" },
                                                        ],
                                                    });
                                                    return;
                                                }
                                                setActivePill({
                                                    uploadContext: msg.uploadContext!,
                                                    mortgageDocumentId: resolved.mortgageDocumentId,
                                                    contactId: resolved.contactId,
                                                    category: resolved.category,
                                                    name: msg.uploadLabel || "Upload Document",
                                                });
                                            }}
                                        >
                                            <Paperclip size={16} style={{ marginRight: 6 }} />
                                            {msg.uploadLabel || "Click to upload your document"}
                                        </button>
                                    ) : (
                                        <label className="chatwindow-upload__label">
                                            <div className="chatwindow-upload__content">
                                                <div className="chatwindow-upload__icon-wrap">
                                                    <Paperclip />
                                                </div>
                                                <p className="chatwindow-upload__text">{msg.uploadLabel || "Click to upload your document"}</p>
                                            </div>
                                            <input
                                                type="file"
                                                className="chatwindow-upload__input"
                                                accept=".pdf,.png,.jpg,.jpeg"
                                                onChange={handleFileUpload}
                                            />
                                        </label>
                                    )}
                                </div>
                            )}

                            {/* File Preview */}
                            {msg.type === "file_preview" && (
                                <div className="chatwindow-file-preview">
                                    <div className="chatwindow-file-preview__icon">
                                        <FileText />
                                    </div>
                                    <span className="chatwindow-file-preview__name">{msg.file?.name || msg.text}</span>
                                </div>
                            )}

                            {/* Book Call */}
                            {/*msg.type === "book_call" && (
                                <div className="chatwindow-link">
                                    <Link to="#" className="p-0">
                                    <button
                                        className="btn btn-primary btn-xxl-wide bigger btn-standard nowrap ps-3 text-start"
                                        disabled={
                                        !context.mortgage?.owner?.bookingCalendarLink
                                        }
                                        onClick={() => {
                                        this.setState({ showBooking: true });
                                        }}
                                    >
                                        <i className="far fa-calendar-days fa-xl pe-2" /> Book
                                        a call with your advisor
                                    </button>
                                    </Link>
                                </div>
                            )*/}


                        </div>
                    </div>
                ))}
                <div ref={messagesEndRef} />
            </div>

            {/* Input Area */}
            <div className="chatwindow-input-area">
                <form onSubmit={handleTextSubmit}>
                    <input
                        type={pendingPin ? "password" : "text"}
                        value={inputValue}
                        onChange={(e) => setInputValue(e.target.value)}
                        disabled={inputState === "none"}
                        placeholder={pendingPin ? "Enter document password..." : "Type your message..."}
                        className="chatwindow-input"
                    />
                    <button
                        type="submit"
                        disabled={inputState === "none" || !inputValue.trim()}
                        className="chatwindow-send-btn"
                    >
                        <Send />
                    </button>
                </form>
                
            </div>

<Modal
        size="lg"
        fullscreen="lg-down"
        show={showBooking}
        centered
        onHide={() => setShowBooking(false)}
        >
        <ModalHeader closeButton className="-mt-2"></ModalHeader>
        <ModalBody
            style={{ background: "transparent", height: "calc(100vh - 100px)" }}
        >
            <iframe
            src="https://outlook.office365.com/owa/calendar/bookacallwithshastri@doddl.ie/bookings/"
            width="100%"
            //height="100%"
            scrolling="yes"
            style={{ border: 0, height: "calc(100% - 40px)", marginTop: 20 }}
            ></iframe>
        </ModalBody>
        </Modal>

        {/* Upload modal — same upload control as DocumentPill, opened from chat */}
        <Modal
            size="xl"
            show={!!activePill}
            onHide={() => handlePillDone(null)}
            centered
        >
            <Modal.Header closeButton>
                <Modal.Title>{activePill?.name}</Modal.Title>
            </Modal.Header>
            <Modal.Body className="px-4 py-4">
                {activePill && mortgage?.mortgageId && (() => {
                    const doc = allDocs.find(d => d.mortgageDocumentId === activePill.mortgageDocumentId);
                    const existingFiles = (userCtx?.files || []).filter(
                        f => f.mortgageDocumentId === activePill.mortgageDocumentId,
                    );
                    return (
                        <>
                            <h4 className="pb-2">Upload {activePill.uploadContext.applicantName}'s {activePill.name}</h4>
                            {doc?.helpText && (
                                <p className="text-muted pb-2" dangerouslySetInnerHTML={{ __html: doc.helpText }} />
                            )}
                            {existingFiles.length > 0 && (
                                <div className="chatwindow-upload-files">
                                    <div className="chatwindow-upload-files__label">Previously uploaded:</div>
                                    {existingFiles.map((f) => (
                                        <div key={f.id} className="chatwindow-upload-files__item">
                                            <FileText size={14} />
                                            <span className="chatwindow-upload-files__name">{f.name}</span>
                                            <span className={`chatwindow-upload-files__status chatwindow-upload-files__status--${f.status === "Approved" ? "approved" : "pending"}`}>
                                                {f.status === "Approved" ? "Approved" : "For review"}
                                            </span>
                                        </div>
                                    ))}
                                </div>
                            )}
                            <p className="text-muted" style={{ fontSize: 12, fontStyle: "italic" }}>
                                We use document intelligence to validate and classify your documents. This may take a little longer than usual, but it helps us fast-track your application.
                            </p>
                        </>
                    );
                })()}
                {activePill && mortgage?.mortgageId && (
                    mortgage.useLegacyUploader ? (
                        <FileUploadLegacy
                            mortgageId={mortgage.mortgageId}
                            contactId={activePill.contactId}
                            category={activePill.category}
                            mortgageDocumentId={activePill.mortgageDocumentId}
                            onUploadComplete={(d: UploadResponse, uploadId: any) => {
                                handlePillDone({ url: d.url, accepted: d.accepted, message: d.message, uploadId, docattributes: d.docattributes });
                            }}
                            onDeleteComplete={() => {}}
                        />
                    ) : (
                        <FileUpload
                            mortgageId={mortgage.mortgageId}
                            contactId={activePill.contactId}
                            category={activePill.category}
                            mortgageDocumentId={activePill.mortgageDocumentId}
                            onUploadComplete={(d: UploadResponse, uploadId: any) => {
                                handlePillDone({ url: d.url, accepted: d.accepted, message: d.message, uploadId, docattributes: d.docattributes });
                            }}
                        />
                    )
                )}
            </Modal.Body>
        </Modal>

        </div>
    
    );
}
